/**
 * CreateTicket - Full ticket creation page for the Pro Portal
 * Adapted from Thaklis app NewTicket page to use ThaklisApiService
 *
 * Key differences from original:
 * - Organization selector at top (managers portal specific)
 * - ThaklisApiService instead of AppService (returns full axios response, NOT unwrapped)
 * - Local state for loading/success/error (MUI Snackbar) instead of Redux dispatch
 * - Direct MUI components instead of WDBox/WDButton/WDTypography
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Grid, Select, MenuItem, TextField, InputLabel, FormControl, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, Typography, Avatar, Divider, Chip,
  Card, CardContent, CircularProgress, Alert, Snackbar
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import { object, string } from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { MdPerson } from 'react-icons/md';

import { Navigate } from 'react-router-dom';
import ApiService from '../../services/api.service';
import ThaklisApiService from '../../services/thaklis-api.service';
import { can } from '../../utils/roleUtils';
import { FieldStructureProvider, useFieldStructure } from './FieldStructureContext';
import {
  STAT_FIELDS, MEDICAL_LOCATIONS, FIELD_CATEGORIES, CATEGORY_DISPLAY_NAMES,
  CONTEXT_TYPES, STATIC_FIELD_CATEGORY_MAP, SPECIFIC_DOCUMENT_FIELDS,
  STATIC_DOCUMENT_FIELDS, DOCUMENT_TYPE_MAPPING, MULTI_INSTANCE_CERTIFICATE_FIELDS,
  SUBMIT_DOCUMENT_FIELDS
} from './constants';
import {
  pxToRem, isDocumentField, ensureConsistentFieldCategorization,
  processServiceFields, processStaticFields, combineFieldsWithoutDuplicates,
  createFieldStructure, processFieldsIntoStructure, storeFieldStructure,
  setCurrentContext, handleEmployeeSelectionUtil, createFieldLabel
} from './utils';
import { headerStyle, cardStyle, cardContentStyle } from './styles';
import FileField from './panels/FileField';
import FieldText from './panels/FieldText';
import CustomerTextField from './panels/CustomerStatFieldInfo';
import QuickEmployeeSearch from './components/QuickEmployeeSearch';
import QuickEmployeeAddDialog from './components/QuickEmployeeAddDialog';
import AuthenticatedImage from './components/AuthenticatedImage';

dayjs.extend(utc);

const initialValues = {
  description: '',
  serviceId: '',
  employeeId: '',
  assignedTo: 'THAKLIS',
  uid: '',
  firstNameEn: '', middleNameEn: '', lastNameEn: '',
  firstNameAr: '', middleNameAr: '', lastNameAr: '',
  nameEn: '', nameAr: '',
  dob: null, gender: '', nationality: '',
  passportNumber: '', passportIssue: '', passportExpiry: null,
  passportExpiry2: null, passportIssuePlaceEn: '', passportIssuePlaceAr: '',
  emiratesId: '', emirates_id_number: '',
  emiratesIdIssueDate: null, emiratesIdExpiryDate: null, emirates_id_expiry_date: null,
  birthPlaceEn: '', birthPlaceAr: '', birthCertificate: '',
  emirate: '', medicalLocation: '',
  address: '', area: '', buildingNumber: '', poBox: '',
  mobileNumber: '', landNumber: '', deliveryNumber: '',
  email: '', fileNumber: '',
  passportCopy: '', visaPage: '',
  visaIssueDate: null, visaExpiryDate: null,
  photo: '', shinasnama: '', labourContract: '', salaryCertificate: ''
};

const validationSchema = object({
  serviceId: string().required('Service is Mandatory')
});

const dateFields = ['dob', 'passportExpiry', 'emiratesIdIssueDate', 'emiratesIdExpiryDate', 'visaIssueDate', 'visaExpiryDate'];

/**
 * Inner form component that uses FieldStructureContext
 */
const TicketForm = ({
  organizationId, organizationName,
  embedded = false, lockedServiceId = null, lockedServiceName = '',
  prefillEmployeeName = '', onCreated = null, onBusyChange = null
}) => {
  const fieldStructureContext = useFieldStructure();
  const { initializeStructure, updateContext, shouldShowFieldInSection } = fieldStructureContext;

  const [services, setServices] = useState([]);
  const [fields, setFields] = useState([]);
  const [chargeMapping, setChargeMapping] = useState();
  const ticketAssignedTo = 'THAKLIS';
  const [uploadingStaticDocs, setUploadingStaticDocs] = useState([]);
  const [documentRequired, setDocumentRequired] = useState([]);
  const [additionalInfoRequired, setAdditionalInfoRequired] = useState([]);
  const [populatedFields, setPopulatedFields] = useState(new Set());
  const [processedDocumentFields, setProcessedDocumentFields] = useState(new Map());
  const formikRef = useRef();

  const [staticFields, setStaticFields] = useState([]);
  const [uid, setUid] = useState('');
  const [fieldCategories, setFieldCategories] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openQuickAddEmployee, setOpenQuickAddEmployee] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    uid: '', firstNameEn: '', middleNameEn: '', lastNameEn: '',
    nameEn: '', mobileNumber: '', email: ''
  });
  const [isUidready, uidReady] = useState(false);

  // Local loading/notification state (replaces Redux)
  const [pageLoading, setPageLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSuccess = (msg) => setSnackbar({ open: true, message: msg, severity: 'success' });
  const showError = (msg) => setSnackbar({ open: true, message: msg, severity: 'error' });

  // Initialize medicalLocation in Formik when it appears in the fields list
  useEffect(() => {
    const hasMedicalLocation = fields.some(f => f.name === 'medicalLocation');
    if (hasMedicalLocation && formikRef.current && !formikRef.current.values?.medicalLocation) {
      formikRef.current.setFieldValue('medicalLocation', MEDICAL_LOCATIONS[0]);
    }
  }, [fields]);

  // Embedded mode: a service is pre-selected and locked. Seed it into Formik and load
  // its field structure once on mount so the user only fills applicant/document data.
  useEffect(() => {
    if (embedded && lockedServiceId && formikRef.current) {
      formikRef.current.setFieldValue('serviceId', lockedServiceId);
      loadServiceFields(lockedServiceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedServiceId, embedded]);

  // Report busy state up to an embedding host (the WO ticket dialog) so it can scope
  // the loading overlay to the popup instead of the page-wide fixed overlay below.
  useEffect(() => {
    if (onBusyChange) onBusyChange(pageLoading);
  }, [pageLoading, onBusyChange]);

  // Track processed document fields
  const trackDocumentField = (fieldName, documentUrl) => {
    setProcessedDocumentFields(prev => {
      const updated = new Map(prev);
      updated.set(documentUrl || fieldName, fieldName);
      return updated;
    });
  };

  const isDocumentFieldProcessed = (fieldName, documentUrl) => {
    return processedDocumentFields.has(documentUrl || fieldName);
  };

  // Check if field is document type (local helper matching original logic)
  const isDocField = (field) => {
    if (!field) return false;
    if (field.type === 'DOCUMENT' || field.type === 'FILE') return true;
    if (field.name && STAT_FIELDS[field.name]) return STAT_FIELDS[field.name].type === 'FILE';
    const docNames = ['passportCopy', 'visaPage', 'birthCertificate', 'salaryCertificate', 'labourContract', 'photo', 'shinasnama'];
    if (field.name && docNames.includes(field.name)) return true;
    const docKeywords = ['file', 'document', 'certificate', 'upload', 'photo', 'signature', 'copy', 'scan', 'passport'];
    if (field.name && docKeywords.some(kw => field.name.toLowerCase().includes(kw))) return true;
    return false;
  };

  // Load services
  useEffect(() => {
    ThaklisApiService.get('v1/serviceMapping/mappedServices')
      .then((response) => {
        // ThaklisApiService returns full axios response
        const data = response.data;
        setServices(Array.isArray(data) ? data : (data?.data || []));
      })
      .catch(err => console.error('Error loading services:', err));
  }, []);


  // Load field categories
  useEffect(() => {
    const defaultCategories = Object.keys(FIELD_CATEGORIES).map(key => ({
      code: key,
      displayName: CATEGORY_DISPLAY_NAMES[key] || key,
      displayOrder: Object.keys(FIELD_CATEGORIES).indexOf(key)
    }));

    ThaklisApiService.get('api/field-categories')
      .then(res => {
        const cats = res.data;
        if (cats && Array.isArray(cats) && cats.length > 0) {
          setFieldCategories(cats);
        } else {
          setFieldCategories(defaultCategories);
        }
      })
      .catch(() => setFieldCategories(defaultCategories));
  }, []);

  // Handle employee removal
  const handleEmployeeRemove = () => {
    setSelectedEmployee(null);
    setUid('');
    window.currentEmployeeUid = null;
    if (formikRef?.current) {
      formikRef.current.setFieldValue('employeeId', '');
      formikRef.current.setFieldValue('employeeUid', '');
    }
    updateContext(CONTEXT_TYPES.DEFAULT);
    setPopulatedFields(new Set());
  };

  // Quick add employee callback
  const handleEmployeeAdded = (newEmployee) => {
    setOpenQuickAddEmployee(false);
    if (newEmployee) handleEmployeeSelect(newEmployee);
  };

  // Helper to auto-fill a field
  const autoFillField = (fieldName, value, documentGroupId = null) => {
    if (value === null || value === undefined) return;
    const fieldExists = fields.find(f => f.name === fieldName);
    if (!fieldExists) return;

    let processedValue = value;
    const isDateFieldName = fieldName.toLowerCase().includes('date') ||
      fieldName.toLowerCase().includes('expiry') ||
      fieldName.toLowerCase().includes('issue');

    if (isDateFieldName && typeof value === 'string' && value.trim() !== '') {
      try { processedValue = dayjs(value); } catch { processedValue = value; }
    }

    formikRef.current.setFieldValue(fieldName, processedValue);
    setPopulatedFields(prev => new Set([...prev, fieldName]));
    setFields(prevFields => prevFields.map(f => {
      if (f.name === fieldName) return { ...f, value, documentGroupId: documentGroupId || f.documentGroupId };
      return f;
    }));
  };

  // Auto-populate from backend grouped docs + employee profile
  const autoPopulateFromBackend = async (employeeUid) => {
    const populatedFromGrouped = new Set();
    if (!employeeUid) return populatedFromGrouped;

    try {
      // Run grouped-docs and employee profile in parallel
      const [groupedResp, profileResp] = await Promise.all([
        ThaklisApiService.get(`v1/employee-documents/${employeeUid}/grouped`),
        ThaklisApiService.get(`v1/employees/${employeeUid}`).catch(() => null)
      ]);

      // Auto-fill from employee profile
      try {
        const employeeData = profileResp?.data?.data || profileResp?.data || null;
        if (employeeData && formikRef?.current) {
          const currentValues = formikRef.current.values || {};
          const isEmpty = (v) => v === undefined || v === null || String(v).trim() === '';

          if (Object.prototype.hasOwnProperty.call(currentValues, 'email') && isEmpty(currentValues.email) && employeeData.email) {
            formikRef.current.setFieldValue('email', employeeData.email);
            setPopulatedFields(prev => new Set([...prev, 'email']));
          }
          if (Object.prototype.hasOwnProperty.call(currentValues, 'passportNumber') && isEmpty(currentValues.passportNumber) && employeeData.passportNumber) {
            formikRef.current.setFieldValue('passportNumber', employeeData.passportNumber);
            setPopulatedFields(prev => new Set([...prev, 'passportNumber']));
          }
          if (Object.prototype.hasOwnProperty.call(currentValues, 'nameEn') && isEmpty(currentValues.nameEn) && employeeData.fullName) {
            formikRef.current.setFieldValue('nameEn', employeeData.fullName);
            setPopulatedFields(prev => new Set([...prev, 'nameEn']));
          }
          if (Object.prototype.hasOwnProperty.call(currentValues, 'nationality') && isEmpty(currentValues.nationality) && employeeData.nationality) {
            formikRef.current.setFieldValue('nationality', employeeData.nationality);
            setPopulatedFields(prev => new Set([...prev, 'nationality']));
          }
          const dobValue = employeeData.dob || employeeData.dateOfBirth || null;
          if (Object.prototype.hasOwnProperty.call(currentValues, 'dob') && isEmpty(currentValues.dob) && dobValue) {
            formikRef.current.setFieldValue('dob', dayjs.utc(dobValue, 'YYYY-MM-DD').local());
            setPopulatedFields(prev => new Set([...prev, 'dob']));
          }

          // Gender auto-fill
          if (employeeData.gender) {
            const genderRaw = String(employeeData.gender);
            if (Object.prototype.hasOwnProperty.call(currentValues, 'gender')) {
              formikRef.current.setFieldValue('gender', genderRaw);
            }
          }
        }
      } catch (e) {
        console.warn('Gender/profile autofill skipped:', e?.message);
      }

      // Process grouped documents
      const responseData = groupedResp?.data?.data || groupedResp?.data;
      if (responseData && responseData.groups && responseData.groups.length > 0) {
        let populatedFieldsCount = 0;

        responseData.groups.forEach((group) => {
          if (group.common && group.common.isCurrent && group.documentGroupId) {
            const documentType = group.common.documentType;
            const common = group.common;
            const documentGroupId = group.documentGroupId;
            // Static document fields (passportCopy, resume, etc.) live in `staticFields` (rendered by
            // CustomerStatFieldInfo) and may not be in the dynamic `fields` array — include both.
            const availableFieldNames = [
              ...fields.map(f => f.name),
              ...(Array.isArray(staticFields) ? staticFields : []),
            ];
            const docTypeLower = documentType.toLowerCase();

            // Generate field name variants
            const possibleFieldNames = [
              documentType, docTypeLower,
              docTypeLower.replace(/_/g, ''),
              docTypeLower.replace(/_([a-z])/g, (_, l) => l.toUpperCase()),
              docTypeLower.replace('_copy', 'Copy').replace('_page', 'Page').replace('_certificate', 'Certificate').replace('_contract', 'Contract').replace('_id', 'Id')
            ].filter(Boolean);

            let fieldName = null;
            for (const variant of possibleFieldNames) {
              if (availableFieldNames.includes(variant)) { fieldName = variant; break; }
            }
            if (!fieldName) {
              for (const af of availableFieldNames) {
                if (STAT_FIELDS[af]) {
                  const fl = af.toLowerCase();
                  if (docTypeLower.split('_').some(w => fl.includes(w))) { fieldName = af; break; }
                }
              }
            }

            const isDocFieldType = fieldName && (
              (STAT_FIELDS[fieldName] && STAT_FIELDS[fieldName].type === 'FILE') ||
              (fieldName === 'emiratesId' && documentType === 'emirates_id' && group.documents?.length > 0)
            );

            if (isDocFieldType) {
              if (group.documents && group.documents.length > 0) {
                const documentReferences = group.documents.map(d => ({
                  fieldName, documentGroupId, documentType: fieldName,
                  id: d.id, documentId: d.id, fileName: d.fileName,
                  filePath: d.filePath, contentType: d.contentType,
                  fileSize: d.fileSize, isPrimary: !!d.isPrimary, uploadDate: d.uploadDate
                }));
                const primaryDoc = group.documents.find(doc => doc.isPrimary) || group.documents[0];
                const fileMetadata = {
                  id: primaryDoc.id, fileName: primaryDoc.fileName,
                  filePath: primaryDoc.filePath, contentType: primaryDoc.contentType,
                  fileSize: primaryDoc.fileSize, documentType: fieldName,
                  employeeUid: responseData.uid, documentReferences,
                  hasMultipleFiles: group.documents.length > 1,
                  totalFiles: group.documents.length
                };
                autoFillField(fieldName, fileMetadata, documentGroupId);
                populatedFromGrouped.add(fieldName);
                populatedFieldsCount++;
              }
            } else if (common.documentNumber && fieldName) {
              autoFillField(fieldName, common.documentNumber, documentGroupId);
              populatedFromGrouped.add(fieldName);
              populatedFieldsCount++;
            }

            // Auto-populate related date fields
            if (fieldName) {
              const baseFieldName = docTypeLower.replace('_', '');
              if (common.issueDate) {
                const variants = [`${fieldName}IssueDate`, `${fieldName}Issue`, `${baseFieldName}IssueDate`];
                if (docTypeLower.includes('visa')) variants.unshift('visaIssueDate', 'visaIssue');
                variants.forEach(v => { if (availableFieldNames.includes(v)) { autoFillField(v, common.issueDate, documentGroupId); populatedFieldsCount++; } });
                if (docTypeLower.includes('visa') && availableFieldNames.includes('visaIssueDate')) autoFillField('visaIssueDate', common.issueDate, documentGroupId);
              }
              if (common.expiryDate) {
                const variants = [`${fieldName}ExpiryDate`, `${fieldName}Expiry`, `${baseFieldName}ExpiryDate`];
                if (docTypeLower.includes('visa')) variants.unshift('visaExpiryDate', 'visaExpiry');
                variants.forEach(v => { if (availableFieldNames.includes(v)) { autoFillField(v, common.expiryDate, documentGroupId); populatedFieldsCount++; } });
                if (docTypeLower.includes('visa') && availableFieldNames.includes('visaExpiryDate')) autoFillField('visaExpiryDate', common.expiryDate, documentGroupId);
              }
            }
          }
        });

        // Multiple-instance certificates (educational/experience) can have several current groups —
        // one per title. The generic loop above maps each group to a single field and overwrites, so
        // aggregate every current group's documents into one field value so all certificates pre-load.
        try {
          const multiInstanceTargets = [
            { name: 'educationalCertificate', match: (dt) => dt.includes('education') && dt.includes('cert') },
            { name: 'experienceCertificate', match: (dt) => dt.includes('experience') && dt.includes('cert') },
          ];
          const isOnForm = (name) => (Array.isArray(staticFields) && staticFields.includes(name)) || fields.some(f => f.name === name);
          multiInstanceTargets.forEach(({ name, match }) => {
            if (!isOnForm(name)) return;
            const grps = responseData.groups.filter(g => g?.common?.isCurrent && g?.documentGroupId && match((g.common.documentType || '').toLowerCase()));
            if (grps.length === 0) return;
            const refs = [];
            grps.forEach(gr => (Array.isArray(gr.documents) ? gr.documents : []).forEach(d => refs.push({
              id: d.id,
              documentId: d.id,
              fileName: d.fileName,
              filePath: d.filePath,
              documentType: name,
              documentGroupId: gr.documentGroupId,
              isPrimary: !!d.isPrimary,
            })));
            if (refs.length === 0) return;
            const primary = refs.find(r => r.isPrimary) || refs[0];
            formikRef.current.setFieldValue(name, {
              isReference: true,
              documentType: name,
              employeeUid: responseData.uid || employeeUid,
              id: primary.id,
              fileName: primary.fileName || name,
              documentGroupId: primary.documentGroupId,
              hasMultipleFiles: refs.length > 1,
              totalFiles: refs.length,
              documentReferences: refs,
            });
            populatedFromGrouped.add(name);
            setPopulatedFields(prev => new Set([...prev, name]));
          });
        } catch (e) {
          console.warn('Multi-instance certificate autofill failed:', e?.message || e);
        }

        if (populatedFieldsCount > 0) {
          showSuccess(`Auto-populated ${populatedFieldsCount} fields from employee documents`);
        }
      }
    } catch (error) {
      console.error('Employee documents API call failed:', error);
    }
    return populatedFromGrouped;
  };

  // Handle employee selection
  const handleEmployeeSelect = async (employee) => {
    if (!employee) { handleEmployeeRemove(); return; }

    setNewCustomer(prev => ({
      ...prev,
      mobileNumber: employee.mobileNumber || prev.mobileNumber,
      firstNameEn: employee.firstNameEn || prev.firstNameEn,
      middleNameEn: employee.middleNameEn || prev.middleNameEn,
      lastNameEn: employee.lastNameEn || prev.lastNameEn,
      email: employee.email || prev.email
    }));

    window.currentEmployeeUid = employee.uid;
    updateContext(CONTEXT_TYPES.EMPLOYEE_SELECTED);

    const updatedStructure = handleEmployeeSelectionUtil(
      employee, formikRef, setFields, setUid, uidReady,
      setPopulatedFields, setSelectedEmployee
    );

    if (updatedStructure) {
      showSuccess('Employee selected successfully');
      const populatedFromGrouped = await autoPopulateFromBackend(employee.uid);

      // Auto-fill static fields from employee data
      if (fields.length > 0) {
        const staticFieldsToFill = ['mobileNumber', 'firstNameEn', 'lastNameEn', 'middleNameEn', 'email', 'nameEn', 'address'];
        const documentFieldsToFill = ['passportCopy', 'visaPage', 'birthCertificate', 'salaryCertificate', 'labourContract', 'emiratesId', 'photo'];

        fields.forEach(field => {
          if (staticFieldsToFill.includes(field.name)) {
            if (employee[field.name] !== undefined && employee[field.name] !== null) {
              if (field.name === 'emiratesId') {
                const emiratesIdPattern = /^784[-]?\d{4}[-]?\d{7}[-]?\d$/;
                if (!emiratesIdPattern.test(employee[field.name])) return;
              }
              formikRef.current.setFieldValue(field.name, employee[field.name]);
              setPopulatedFields(prev => new Set([...prev, field.name]));
              setFields(prev => prev.map(f => f.name === field.name ? { ...f, value: employee[field.name] } : f));
            }
          } else if (documentFieldsToFill.includes(field.name)) {
            if (populatedFromGrouped?.has(field.name) || populatedFields?.has(field.name)) return;
            // Document auto-fill handled by autoPopulateFromBackend
          }
        });
      }
    } else {
      formikRef.current.setFieldValue('employeeId', '');
    }
  };

  // Service change handler
  const onServiceChange = async (event, setFieldValue) => {
    const serviceId = event.target.value;
    setFieldValue('serviceId', serviceId);
    await loadServiceFields(serviceId);
  };

  // Load the field structure for a service. Shared by the dropdown handler and the
  // embedded (locked-service) flow so a pre-selected service loads its fields on mount.
  const loadServiceFields = async (serviceId) => {
    if (!serviceId) return;
    setPageLoading(true);
    try {
      // Fetch service fields from Thaklis API
      const response = await ThaklisApiService.get('v1/serviceField/byService', { params: { serviceId } });
      const data = response.data;

      // Extract service fields and static fields
      const rawServiceFields = data?.serviceFields || data?.fields || [];
      const rawStaticFields = data?.staticFields || [];
      const chargeData = data?.chargeMapping || null;

      // Process fields
      const processedServiceFields = processServiceFields(rawServiceFields);
      const processedStaticFields = processStaticFields(rawStaticFields);
      const { allFields } = combineFieldsWithoutDuplicates(processedServiceFields, processedStaticFields);

      // Apply consistent field categorization
      const categorizedFields = ensureConsistentFieldCategorization(allFields);

      // Create field structure
      const structure = createFieldStructure(serviceId);
      const updatedStructure = processFieldsIntoStructure(structure, categorizedFields);
      storeFieldStructure(updatedStructure);

      // Update state
      setFields(categorizedFields);
      setStaticFields(rawStaticFields);
      setAdditionalInfoRequired(data?.additionalInfoRequired || []);
      setDocumentRequired(data?.documentRequired || []);
      setChargeMapping(chargeData);

      // Initialize field structure context
      initializeStructure(updatedStructure);
      updateContext(CONTEXT_TYPES.DEFAULT);
      setSelectedEmployee(null);
    } catch (error) {
      console.error('Error in service selection:', error);
      showError('Error loading service fields. Please try again.');
    } finally {
      setPageLoading(false);
    }
  };

  const _onTextChange = (text, field, index) => {
    const fieldsCopy = fields.map(item => item.id === field.id ? { ...item, value: text } : item);
    setFields(fieldsCopy);
  };

  const _onFileUpload = (fileUrl, field) => {
    const hasDocumentGroupId = fileUrl && typeof fileUrl === 'object' && fileUrl.documentGroupId;
    const fieldsCopy = fields.map(item => {
      if (item.id === field.id) {
        const updated = { ...item, value: fileUrl };
        if (hasDocumentGroupId) updated.documentGroupId = fileUrl.documentGroupId;
        if (hasDocumentGroupId && selectedEmployee?.uid) {
          if (!fileUrl.employeeUid) fileUrl.employeeUid = selectedEmployee.uid;
          if (!fileUrl.documentType && item.name) {
            fileUrl.documentType = item.name.replace(/([A-Z])/g, '_$1').toLowerCase();
          }
        }
        return updated;
      }
      return item;
    });
    setFields(fieldsCopy);
    if (hasDocumentGroupId && formikRef.current) {
      formikRef.current.setFieldValue(field.name, fileUrl);
    }
  };

  const _onFileRemove = (field) => {
    const fieldsCopy = fields.map(item => item.id === field.id ? { ...item, value: null } : item);
    setFields(fieldsCopy);
    if (formikRef.current) formikRef.current.setFieldValue(field.name, null);
    if (field.name) setPopulatedFields(prev => { const s = new Set(prev); s.delete(field.name); return s; });
  };

  const _isFieldsAreValid = () => {
    const missing = fields.filter(f => f.mandatory && !f.value);
    return missing.length === 0;
  };

  // Validate required documents
  const validateRequiredDocuments = (values) => {
    const criticalDocs = ['passportCopy', 'visaPage', 'emiratesId'];
    const allRequired = [...new Set([...(documentRequired || []), ...criticalDocs])];
    const docFieldsToCheck = fields.filter(f => allRequired.includes(f.name) && f.type === 'FILE');
    const missing = docFieldsToCheck.filter(f => {
      const v = values[f.name] || f.value;
      return !(v && ((typeof v === 'object' && v.isReference && v.id) || (typeof v === 'string' && v.trim() !== '')));
    });
    return { isValid: missing.length === 0, missingDocuments: missing.map(f => f.label || f.name) };
  };

  // Render a single field
  const _renderField = (field, index) => {
    if (!field || !field.name || field.name === 'undefined') return null;

    let fieldLabel = field.label || field.title;
    if (!fieldLabel) {
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (field.name && uuidPattern.test(field.name)) {
        fieldLabel = field.displayName || field.fieldName || `Service Field ${index + 1}`;
      } else if (field.name) {
        fieldLabel = createFieldLabel(field.name);
      } else {
        fieldLabel = `Field ${index + 1}`;
      }
    }

    const enhancedField = { ...field, label: fieldLabel, title: fieldLabel };
    if (field.label && (field.label.includes('undefined') || field.label.includes('null'))) return null;

    const correctFieldType = STAT_FIELDS[field.name]?.type || field.type;
    const shouldRenderAsText = ['TEXT', 'DATE', 'NUMBER', 'PHONE'].includes(correctFieldType);

    if (correctFieldType === 'SELECT' && field.name === 'medicalLocation') {
      return (
        <Grid item xs={12} sm={12} md={6} key={`field-${enhancedField.id}-${index}`}>
          <Box height="100%" sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="medical-location-select-label">Medical Location</InputLabel>
              <Select
                labelId="medical-location-select-label"
                name="medicalLocation" label="Medical Location"
                value={enhancedField.value || MEDICAL_LOCATIONS[0]}
                onChange={(e) => {
                  _onTextChange(e.target.value, enhancedField, index);
                  formikRef.current.setFieldValue('medicalLocation', e.target.value);
                }}
                fullWidth
              >
                {MEDICAL_LOCATIONS.map(loc => <MenuItem key={loc} value={loc}>{loc}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      );
    }

    if (shouldRenderAsText) {
      return (
        <Grid item xs={12} sm={12} md={6} key={`field-${enhancedField.id}-${index}`}>
          <Box height="100%" sx={{ mb: 2 }}>
            <FieldText index={index} field={enhancedField} onChange={_onTextChange} contextProps={fieldStructureContext} />
          </Box>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={12} md={6} key={`field-${enhancedField.id}-${index}`}>
        <Box height="100%" sx={{ mb: 2 }}>
          <FileField
            index={index} field={enhancedField}
            onFileUpload={_onFileUpload} onFileRemove={_onFileRemove}
            isProcessed={isDocumentFieldProcessed(enhancedField.name)}
            employeeUid={selectedEmployee?.uid || selectedEmployee?.employeeId || ''}
            selectedEmployee={selectedEmployee}
            contextProps={fieldStructureContext}
          />
        </Box>
      </Grid>
    );
  };

  // UID details lookup
  const _getUidDetails = () => {
    const uidValue = uid.trim();
    if (!uidValue) { showError('Please enter valid UID'); return; }
    formikRef.current.setFieldValue('uid', uidValue);
    setPageLoading(true);

    ThaklisApiService.get('/api/customers/lookup', { params: { uid: uidValue } })
      .then((response) => {
        const customerData = response.data?.data || response.data;
        const validUid = customerData.uid || uidValue;
        setUid(validUid);
        if (formikRef.current) formikRef.current.setFieldValue('uid', validUid);
        uidReady(true);

        setTimeout(() => {
          if (formikRef.current) {
            formikRef.current.setFieldValue('uid', customerData.uid || '');
            const customerName = customerData.nameEn || customerData.name ||
              [customerData.firstNameEn, customerData.middleNameEn, customerData.lastNameEn].filter(Boolean).join(' ');
            formikRef.current.setFieldValue('customerName', customerName.trim());
            formikRef.current.setFieldValue('customerId', customerData.id || customerData.customerId || '');
            formikRef.current.setFieldValue('mobileNumber', customerData.mobileNumber || customerData.mobile || '');
            formikRef.current.setFieldValue('email', customerData.email || '');

            ['firstNameEn', 'middleNameEn', 'lastNameEn', 'nameEn', 'gender', 'passportNumber', 'emiratesId'].forEach(f => {
              if (formikRef.current.values.hasOwnProperty(f) && customerData[f]) {
                formikRef.current.setFieldValue(f, customerData[f]);
              }
            });

            dateFields.forEach(df => {
              try {
                if (customerData[df] && formikRef.current.values.hasOwnProperty(df)) {
                  formikRef.current.setFieldValue(df, dayjs.utc(customerData[df], 'YYYY-MM-DD').local());
                }
              } catch (e) { /* skip date error */ }
            });
          }
          uidReady(true);
          showSuccess('Customer details loaded successfully');
          setPageLoading(false);
        }, 100);
      })
      .catch((error) => {
        const isNotFound = error?.response?.status === 404 ||
          error?.message?.includes('404') ||
          error?.message?.includes('not found');

        if (isNotFound) {
          handleCustomerNotFound(uidValue);
        } else {
          showError(error?.response?.data?.message || 'Error looking up customer');
          uidReady(false);
        }
        setPageLoading(false);
      });
  };

  // Customer not found handler
  const handleCustomerNotFound = (uidValue) => {
    showError(`Customer with UID ${uidValue} not found. Please add customer details.`);
    setNewCustomer({
      uid: uidValue, firstNameEn: '', middleNameEn: '', lastNameEn: '',
      nameEn: '', mobileNumber: '', email: ''
    });
    if (formikRef.current) {
      formikRef.current.resetForm();
      formikRef.current.setFieldValue('uid', uidValue);
    }
    uidReady(false);
    setTimeout(() => setOpenCustomerDialog(true), 100);
  };

  // Customer dialog handlers
  const handleCustomerInputChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...newCustomer, [name]: value };
    if (['firstNameEn', 'middleNameEn', 'lastNameEn'].includes(name)) {
      updated.nameEn = [
        name === 'firstNameEn' ? value : updated.firstNameEn,
        name === 'middleNameEn' ? value : updated.middleNameEn,
        name === 'lastNameEn' ? value : updated.lastNameEn
      ].filter(Boolean).join(' ');
    }
    setNewCustomer(updated);
  };

  const handleCreateCustomer = () => {
    if (!newCustomer.firstNameEn.trim()) { showError('First name is required'); return; }
    if (!newCustomer.lastNameEn.trim()) { showError('Last name is required'); return; }
    if (!newCustomer.mobileNumber.trim()) { showError('Mobile number is required'); return; }

    if (formikRef.current) formikRef.current.setFieldValue('isNewCustomer', true);

    let customerData = { ...newCustomer };
    if (!customerData.nameEn.trim()) {
      customerData.nameEn = [customerData.firstNameEn, customerData.middleNameEn, customerData.lastNameEn].filter(Boolean).join(' ').trim();
    }

    setPageLoading(true);
    ThaklisApiService.post('/api/customers/create', customerData)
      .then((response) => {
        const created = response.data?.data || response.data;
        showSuccess(`Customer ${customerData.firstNameEn} ${customerData.lastNameEn} created successfully`);
        setOpenCustomerDialog(false);

        if (created) {
          Object.keys(created).forEach(key => {
            if (dateFields.includes(key) && created[key]) {
              formikRef.current.setFieldValue(key, dayjs.utc(created[key], 'YYYY-MM-DD').local());
            } else if (created[key]) {
              formikRef.current.setFieldValue(key, created[key]);
            }
          });
          const validUid = created.uid || newCustomer.uid;
          formikRef.current.setFieldValue('uid', validUid);
          setUid(validUid);
          formikRef.current.setFieldValue('isNewCustomer', false);
        }
        uidReady(true);
        setTimeout(() => _getUidDetails(), 500);
      })
      .catch((error) => {
        const status = error?.response?.status;
        if (status === 409) {
          showError('A customer with this UID already exists');
          setTimeout(() => { _getUidDetails(); setOpenCustomerDialog(false); }, 1000);
        } else {
          showError(error?.response?.data?.message || 'Failed to create customer');
        }
      })
      .finally(() => setPageLoading(false));
  };

  // Check if form values contain any file uploads (used to require an employee before submit)
  const hasFileUploads = (values) => {
    if (!values) return false;
    if (fields && fields.length > 0) {
      for (const field of fields) {
        if (field.type === 'file' && values[field.name] &&
            (values[field.name].s3Key || values[field.name].downloadUrl ||
             values[field.name].filePath || values[field.name].fileName)) {
          return true;
        }
      }
    }
    return false;
  };

  // Write newly-uploaded documents back to the employee's document profile just before the ticket is
  // created (mirrors the New Workflow Request form). Files are already on S3 (uploaded at select time);
  // here we bulk-save them so the profile write and the ticket link happen together at submit. Raw
  // single-instance uploads become the employee's current version; multiple-instance certificate popup
  // values are saved one group per titled certificate. `values` is mutated in place so the existing
  // documentReferences-collection below picks up the resulting documentGroupIds.
  const syncNewUploadsToEmployeeDocuments = async (values) => {
    try {
      const empUid = values.employeeUid || selectedEmployee?.uid || selectedEmployee?.employeeId;
      if (!empUid) return;
      const bulkUrl = `v1/employee-documents/${empUid}/bulk`;
      const lastSeg = (p) => (typeof p === 'string' ? p.split('/').pop() : '') || 'Document';
      const multiInstance = new Set(MULTI_INSTANCE_CERTIFICATE_FIELDS);

      for (const code of SUBMIT_DOCUMENT_FIELDS) {
        const value = values[code];
        // Only newly-entered uploads / certificate-popup values that are NOT already employee-doc references.
        if (!value || typeof value !== 'object' || value.isReference) continue;

        // Multiple-instance certificate popup value: bulk-save each titled sub as its own group.
        if (value.isMultiCertificate && Array.isArray(value.subs)) {
          const certBackend = value.backendDocumentType || DOCUMENT_TYPE_MAPPING[code];
          if (!certBackend) continue;
          const allRefs = Array.isArray(value.existingRefs) ? [...value.existingRefs] : [];
          let firstGroupId = allRefs.length ? allRefs[0].documentGroupId : null;
          let primaryDoc = null;
          for (const sub of value.subs) {
            const title = (sub.title || '').trim();
            const files = Array.isArray(sub.files) ? sub.files.filter(f => f.filePath) : [];
            if (!title || files.length === 0) continue;
            try {
              // eslint-disable-next-line no-await-in-loop
              const resp = await ThaklisApiService.post(bulkUrl, {
                documentType: certBackend,
                replaceExisting: false, // add as a new titled certificate group (becomes current)
                title,
                isReplace: false,
                documents: files.map((f, idx) => ({ filePath: f.filePath, fileName: files.length > 1 ? `${title} (${idx + 1})` : title, contentType: f.contentType || null, fileSize: f.fileSize || null })),
              });
              const saved = resp?.data?.data || resp?.data || [];
              const docs = Array.isArray(saved) ? saved : [];
              docs.forEach(d => {
                allRefs.push({ id: d.id, documentId: d.id, fileName: d.fileName, filePath: d.filePath, documentType: code, documentGroupId: d.documentGroupId, isPrimary: !!d.isPrimary });
                if (!firstGroupId) firstGroupId = d.documentGroupId;
                if (!primaryDoc) primaryDoc = d;
              });
            } catch (e) {
              console.warn(`Could not save certificate "${title}" for ${code}; it will be omitted`, e);
            }
          }
          if (allRefs.length > 0) {
            values[code] = {
              isReference: true, documentType: code, backendDocumentType: certBackend, employeeUid: empUid,
              id: primaryDoc ? primaryDoc.id : allRefs[0].id, fileName: primaryDoc ? primaryDoc.fileName : allRefs[0].fileName,
              documentGroupId: firstGroupId, hasMultipleFiles: allRefs.length > 1, totalFiles: allRefs.length, documentReferences: allRefs,
            };
          }
          continue;
        }

        // Single-instance raw upload (has a filePath and is not yet a saved group). A multi-file upload
        // carries all its files in documentReferences with an isNewUpload flag.
        const isNewMultiUpload = value.isNewUpload === true && Array.isArray(value.documentReferences) && value.documentReferences.length > 0;
        const filePath = value.filePath || value.path;
        // Skip values that already reference an employee-profile document. Freshly staged uploads
        // (isNewUpload) are NOT skipped even though they carry documentReferences.
        const alreadyOnProfile = !isNewMultiUpload && (!!value.documentGroupId ||
          (Array.isArray(value.documentReferences) && value.documentReferences.length > 0));
        if ((!filePath && !isNewMultiUpload) || alreadyOnProfile || multiInstance.has(code)) continue;
        const backendType = DOCUMENT_TYPE_MAPPING[code] || code.replace(/([A-Z])/g, '_$1').toLowerCase();
        if (!backendType) continue;
        const singleInstanceDocs = isNewMultiUpload
          ? value.documentReferences.map(r => ({ filePath: r.filePath, fileName: r.fileName || lastSeg(r.filePath), contentType: r.contentType || null, fileSize: r.fileSize || null }))
          : [{ filePath, fileName: value.fileName || lastSeg(filePath), contentType: value.contentType || value.fileType || null, fileSize: value.fileSize || null }];
        try {
          // eslint-disable-next-line no-await-in-loop
          const resp = await ThaklisApiService.post(bulkUrl, {
            documentType: backendType,
            replaceExisting: true, // the freshly uploaded file(s) become the employee's current version
            documents: singleInstanceDocs,
          });
          const saved = resp?.data?.data || resp?.data || [];
          const docs = Array.isArray(saved) ? saved : [];
          if (docs.length === 0) continue;
          const primary = docs.find(d => d.isPrimary) || docs[0];
          values[code] = {
            isReference: true, documentType: code, backendDocumentType: backendType, employeeUid: empUid,
            id: primary.id, fileName: primary.fileName || value.fileName || lastSeg(filePath), documentGroupId: primary.documentGroupId,
            documentReferences: docs.map(d => ({ id: d.id, documentId: d.id, fileName: d.fileName, filePath: d.filePath, documentType: code, documentGroupId: d.documentGroupId, isPrimary: !!d.isPrimary })),
          };
        } catch (e) {
          console.warn(`Could not save uploaded ${code} to employee profile; submitting as a plain upload`, e);
        }
      }
    } catch (e) {
      console.warn('syncNewUploadsToEmployeeDocuments failed', e);
    }
  };

  // Form submit handler
  const _onSubmit = async (values, { resetForm, setSubmitting, setErrors }) => {
    values.assignedTo = ticketAssignedTo;

    if (!selectedEmployee) {
      showError('Please select an employee for this ticket.');
      setSubmitting(false);
      return;
    }

    // Require an employee if there are file uploads
    if (!values.employeeUid && hasFileUploads(values)) {
      setErrors({ employee: 'Please select an employee before submitting the form with file uploads' });
      setSubmitting(false);
      showError('Please select an employee before submitting the form with file uploads');
      return;
    }

    setPageLoading(true);

    // Bulk-save newly uploaded documents to the employee profile just before creating the ticket.
    await syncNewUploadsToEmployeeDocuments(values);

    const uuidKeyRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i; // generic UUID for keys
    const uuidValRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i; // strict for values

    // Document fields that might contain references to existing documents
    const documentFields = SUBMIT_DOCUMENT_FIELDS;

    const fieldsObj = {};
    const documentIds = [];
    const documentReferences = [];
    const collectedDocumentMetadata = [];

    // Preserve document field values for documentReferences collection before clearing.
    const preservedDocumentValues = {};
    documentFields.forEach(fieldName => {
      if (values[fieldName] && typeof values[fieldName] === 'object') {
        preservedDocumentValues[fieldName] = { ...values[fieldName] };
        if (values[fieldName].isReference) {
          values[fieldName] = values[fieldName].id;
        } else if (fieldName === 'emiratesId' && values[fieldName].documentNumber) {
          values[fieldName] = values[fieldName].documentNumber;
        } else if (values[fieldName].fileName || values[fieldName].filePath) {
          values[fieldName] = null;
        }
      }
      const fieldFromState = fields.find(f => f.name === fieldName);
      if (fieldFromState?.value && typeof fieldFromState.value === 'object') {
        if (!preservedDocumentValues[fieldName]) preservedDocumentValues[fieldName] = { ...fieldFromState.value };
        if (fieldFromState.documentGroupId && !preservedDocumentValues[fieldName]?.documentGroupId) {
          preservedDocumentValues[fieldName] = preservedDocumentValues[fieldName] || {};
          preservedDocumentValues[fieldName].documentGroupId = fieldFromState.documentGroupId;
        }
      }
    });

    // Collect document references from preserved values (handles ALL static doc fields).
    documentFields.forEach(fieldName => {
      const fieldValue = preservedDocumentValues[fieldName];
      if (!fieldValue) return;
      const refs = Array.isArray(fieldValue.documentReferences) ? fieldValue.documentReferences : [];
      if (refs.length > 0) {
        refs.forEach((ref) => {
          if (ref?.documentGroupId) {
            documentReferences.push({
              documentGroupId: ref.documentGroupId,
              documentType: fieldName,
              fileName: ref?.fileName || `${fieldName}.pdf`,
              filePath: ref?.filePath || fieldValue?.filePath || null,
              documentId: ref?.id || ref?.documentId || null,
            });
          }
        });
      } else if (fieldValue.documentGroupId) {
        documentReferences.push({
          documentGroupId: fieldValue.documentGroupId,
          documentType: fieldValue.documentType || fieldName,
          fileName: fieldValue.fileName || `${fieldName}.pdf`,
          filePath: fieldValue.filePath || null,
          documentId: fieldValue.id || null,
        });
      }
    });

    // Add service fields - backend expects Map<UUID, String>
    fields.forEach((field) => {
      // Prefer values[field.name] when Formik holds it, else fall back to field.value.
      const fieldValue = (values && field?.name && values[field.name] !== undefined)
        ? values[field.name]
        : field.value;

      if (!uuidKeyRegex.test(field.id)) {
        // Skip non-UUID fields (static fields like passportCopy, visaPage, etc.)
        return;
      }

      const isStaticDocumentField = documentFields.includes(field.name);
      if (isStaticDocumentField) {
        if (fieldValue && typeof fieldValue === 'object') {
          const refs = Array.isArray(fieldValue.documentReferences) ? fieldValue.documentReferences : [];
          if (refs.length > 0) {
            refs.forEach((ref) => {
              if (ref?.documentGroupId) {
                documentReferences.push({
                  documentGroupId: ref.documentGroupId,
                  documentType: field.name,
                  fileName: ref?.fileName || `${field.name}.pdf`,
                  filePath: ref?.filePath || fieldValue?.filePath || null,
                  documentId: ref?.id || ref?.documentId || null,
                  isPrimary: ref?.isPrimary ?? undefined,
                });
                return;
              }
              const docId = ref?.documentId || ref?.id || ref;
              const idStr = typeof docId === 'string' ? docId : (docId?.toString?.() || '');
              if (uuidValRegex.test(idStr)) {
                documentIds.push(idStr);
                documentReferences.push({ documentId: idStr, documentType: field.name, fileName: ref?.fileName || `${field.name}.pdf` });
                collectedDocumentMetadata.push({ id: idStr, fileName: ref?.fileName || `${field.name}.pdf`, fieldName: field.name, documentType: field.name });
              }
            });
          } else if (fieldValue.documentGroupId) {
            documentReferences.push({
              documentGroupId: fieldValue.documentGroupId,
              documentType: fieldValue.documentType || field.name,
              fileName: fieldValue.fileName || `${field.name}.pdf`,
              filePath: fieldValue.filePath || null,
              documentId: fieldValue.id || null,
            });
          } else {
            const fallbackGroupId = field?.documentGroupId;
            if (fallbackGroupId) {
              documentReferences.push({ documentGroupId: fallbackGroupId, documentType: field.name, fileName: `${field.name}.pdf` });
            }
          }
        } else if (!fieldValue && field?.documentGroupId) {
          documentReferences.push({ documentGroupId: field.documentGroupId, documentType: field.name, fileName: `${field.name}.pdf` });
        }
        // Ensure static document fields are NOT sent as top-level values / in fieldsObj
        values[field.name] = '';
        return;
      }

      // Dynamic service fields: reduce object (file metadata) to a plain string (file path).
      let dynamicFieldValue = fieldValue;
      if (dynamicFieldValue && typeof dynamicFieldValue === 'object') {
        dynamicFieldValue = dynamicFieldValue.filePath || dynamicFieldValue.path ||
          dynamicFieldValue.documentUrl || dynamicFieldValue.s3Key || dynamicFieldValue.id || '';
      }
      fieldsObj[field.id] = dynamicFieldValue;
    });

    // If employee ticket but no refs collected yet, derive from fields state (submit-time fallback)
    if (values.employeeUid && documentReferences.length === 0) {
      fields.forEach((fld) => {
        if (documentFields.includes(fld.name) && fld.documentGroupId) {
          documentReferences.push({ documentGroupId: fld.documentGroupId, documentType: fld.name });
        }
      });
    }

    // Format document references for the backend if this is an employee ticket
    if (documentReferences.length > 0 && values.employeeUid) {
      values.documentReferences = documentReferences.map(ref => (
        ref.documentGroupId
          ? { documentGroupId: ref.documentGroupId, documentType: ref.documentType }
          : { id: ref.documentId, documentType: ref.documentType, employeeUid: values.employeeUid }
      ));
    }
    if (collectedDocumentMetadata.length > 0) values.documentMetadata = collectedDocumentMetadata;

    // Document IDs (legacy / non-employee contexts)
    if (documentIds.length > 0) {
      values.documentIds = documentIds;
      values.documentCount = documentIds.length;
      if (selectedEmployee && selectedEmployee.uid) {
        values.documentAssociations = { employeeUid: selectedEmployee.uid, documentIds };
      }
    } else {
      values.documentIds = [];
      values.documentCount = 0;
    }

    values.fields = fieldsObj;

    // UID validation
    if (values.isNewCustomer === true) {
      const missing = ['firstNameEn', 'lastNameEn', 'mobileNumber'].filter(f => !values[f]);
      if (missing.length > 0) {
        showError(`Please provide ${missing.join(', ')} for the new customer.`);
        setPageLoading(false);
        return;
      }
    } else if (values.uid && values.uid.trim() !== '') {
      // OK
    } else if (uid && uid.trim() !== '') {
      values.uid = uid;
    } else {
      showError('Customer UID is required.');
      setPageLoading(false);
      return;
    }

    // Employee data
    const formatEmployeeId = (employee) => {
      if (employee.employeeId && employee.employeeId.trim() !== '') return employee.employeeId;
      if (employee.uid && employee.uid.trim() !== '') {
        const uidParts = employee.uid.split('-');
        if (uidParts.length >= 2) return `${uidParts[0]}-${uidParts[uidParts.length - 1]}`;
        return employee.uid;
      }
      return null;
    };

    const employeeId = formatEmployeeId(selectedEmployee);
    if (!employeeId || !selectedEmployee.uid) {
      showError('Invalid employee data. Please select a valid employee.');
      setPageLoading(false);
      return;
    }

    const employeeDataForSubmission = {
      uid: selectedEmployee.uid,
      employeeId,
      nameEn: selectedEmployee.nameEn || `${selectedEmployee.firstNameEn || ''} ${selectedEmployee.lastNameEn || ''}`.trim(),
      department: selectedEmployee.department?.departmentName || '',
      position: selectedEmployee.position?.positionName || ''
    };

    values.employee = employeeDataForSubmission;
    values.employeeId = employeeId;
    values.employeeUid = selectedEmployee.uid;
    if (selectedEmployee.photo) values.employee.photo = selectedEmployee.photo;

    values.uidDetails = {
      uid: selectedEmployee.uid, type: 'EMPLOYEE',
      firstNameEn: selectedEmployee.firstNameEn || '',
      middleNameEn: selectedEmployee.middleNameEn || '',
      lastNameEn: selectedEmployee.lastNameEn || '',
      nameEn: selectedEmployee.nameEn || employeeDataForSubmission.nameEn,
      gender: selectedEmployee.gender || 'MALE',
      dob: selectedEmployee.dob || selectedEmployee.dateOfBirth || null,
      passportNumber: selectedEmployee.passportNumber || '',
      email: selectedEmployee.email || '',
      mobileNumber: selectedEmployee.mobileNumber || ''
    };

    if (!values.customerName) values.customerName = employeeDataForSubmission.nameEn;
    delete values.customerId;

    // Final payload validation
    const validatePayload = () => {
      const validationErrors = [];
      if (!values.employeeUid && (!values.employee || !values.employee.uid)) validationErrors.push('Employee UID is missing');
      if (!values.customerName) validationErrors.push('Customer Name is missing');
      const documentValidation = validateRequiredDocuments(values);
      if (!documentValidation.isValid) {
        validationErrors.push(`Required documents are missing: ${documentValidation.missingDocuments.join(', ')}`);
      }
      return validationErrors;
    };

    const validationErrors = validatePayload();
    if (validationErrors.length > 0) {
      showError(`Validation failed: ${validationErrors.join(', ')}`);
      setPageLoading(false);
      return;
    }

    // Sanitize the payload
    const sanitizePayload = (vals) => {
      const sanitized = { ...vals };
      const uuidFields = ['serviceId', 'customerId', 'workflowInstanceId', 'workflowStepId', 'workflowRequestId'];
      uuidFields.forEach((field) => {
        if (sanitized[field] && !uuidKeyRegex.test(sanitized[field])) delete sanitized[field];
      });

      if (sanitized.fields && typeof sanitized.fields === 'object') {
        const validFields = {};
        Object.keys(sanitized.fields).forEach((key) => { if (uuidKeyRegex.test(key)) validFields[key] = sanitized.fields[key]; });
        sanitized.fields = validFields;
      }

      const aggregatedDocumentReferences = Array.isArray(vals.documentReferences) ? [...vals.documentReferences] : [];

      // Remove legacy static document fields from top-level payload
      documentFields.forEach((field) => { if (sanitized[field]) delete sanitized[field]; });

      if (!sanitized.documentIds) sanitized.documentIds = [];

      if (sanitized.employeeUid) {
        // Employee tickets rely on group-based documentReferences for static docs
        sanitized.documentIds = [];
        if (sanitized.documentMetadata) delete sanitized.documentMetadata;
        const incomingRefs = Array.isArray(vals.documentReferences) ? vals.documentReferences : [];
        sanitized.documentReferences = incomingRefs
          .filter((ref) => ref && ref.documentGroupId)
          .map((ref) => ({ documentGroupId: ref.documentGroupId, documentType: ref.documentType }));
      } else {
        const seenRefs = new Set();
        sanitized.documentReferences = aggregatedDocumentReferences.filter((ref) => {
          const key = `${ref.documentId}::${ref.fieldName}`;
          if (!ref.documentId || seenRefs.has(key)) return false;
          seenRefs.add(key);
          return true;
        });
      }

      return sanitized;
    };

    const sanitized = sanitizePayload(values);

    // Submit to Thaklis API
    ThaklisApiService.post('v1/ticket', sanitized)
      .then((response) => {
        const result = response.data;
        const createdTicket = result?.data || result;
        // Embedded mode (WO create): hand the created ticket back to the parent so it can
        // auto-link it to the work-order line; don't reset the (about-to-unmount) form.
        if (embedded && onCreated) {
          onCreated(createdTicket);
          return;
        }
        resetForm?.();
        setFields([]);
        setChargeMapping();
        uidReady(false);
        setUid('');
        setSelectedEmployee(null);
        showSuccess(`Ticket created successfully! Ticket Number: ${result?.ticketNumber || result?.data?.ticketNumber || 'N/A'}`);
      })
      .catch((error) => {
        let errorMessage = 'Failed to create ticket. Please try again.';
        if (error.response) {
          const status = error.response.status;
          if (status === 400) {
            errorMessage = error.response.data?.errors
              ? `Validation errors: ${error.response.data.errors.map(e => `${e.field}: ${e.message}`).join(', ')}`
              : 'Invalid ticket data. Please check your inputs.';
          } else if (status === 401 || status === 403) {
            errorMessage = 'Authentication error. Please try again.';
          } else if (status === 500) {
            errorMessage = error.response.data?.message || 'Server error occurred.';
          }
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your network.';
        }
        showError(errorMessage);
      })
      .finally(() => setPageLoading(false));
  };

  const _onStaticFieldDocUpload = (formikField) => {
    setUploadingStaticDocs(prev => [...prev, formikField]);
  };

  const _onStaticFieldDocUploadComplete = (formikField) => {
    setUploadingStaticDocs(prev => prev.filter(item => item !== formikField));
  };

  function onKeyDown(keyEvent) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) keyEvent.preventDefault();
  }

  return (
    <>
      {/* Page-wide overlay for the standalone page. In embedded (WO dialog) mode the
          host scopes the loader to the popup instead — see onBusyChange. */}
      {pageLoading && !embedded && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 9999 }}>
          <CircularProgress />
        </Box>
      )}

      <Formik initialValues={initialValues} onSubmit={_onSubmit} validationSchema={validationSchema} innerRef={formikRef}>
        {({ errors, isValid, touched, dirty, setFieldValue, values }) => (
          <Form onKeyDown={onKeyDown}>
            {/* Ticket Information */}
            <Box sx={cardStyle}>
              <Box sx={headerStyle}>
                <Typography fontSize={16} variant="h6" color="text.primary">Ticket Information</Typography>
              </Box>
              <Divider sx={{ borderColor: 'divider' }} />
              <Box sx={cardContentStyle}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {embedded && lockedServiceId ? (
                      <TextField
                        label="Service"
                        value={lockedServiceName || ''}
                        fullWidth
                        disabled
                        helperText="Service is fixed for this work-order line"
                      />
                    ) : (
                      <FormControl fullWidth>
                        <InputLabel id="service-label">Service</InputLabel>
                        <Field name="serviceId" label="Service" labelId="service-label" fullWidth as={Select}
                          onChange={(e) => onServiceChange(e, setFieldValue)}
                          error={Boolean(errors.serviceId) && Boolean(touched.serviceId)}>
                          {services.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                        </Field>
                      </FormControl>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Field name="description" label="Ticket Description" fullWidth as={TextField}
                      multiline maxRows={2}
                      error={Boolean(errors.description) && Boolean(touched.description)}
                      helperText={Boolean(touched.description) && errors.description} />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Employee Search Section */}
            <Box sx={cardStyle}>
              <Box sx={headerStyle}>
                <Typography fontSize={16} variant="h6" color="text.primary">Employee Search</Typography>
              </Box>
              <Divider sx={{ borderColor: 'divider' }} />
              <Box sx={cardContentStyle}>
                <Field name="employeeId">
                  {({ field }) => (
                    <Box sx={{ position: 'relative' }}>
                      {!selectedEmployee ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <QuickEmployeeSearch
                              selectedEmployee={null}
                              onEmployeeSelect={handleEmployeeSelect}
                              organizationId={organizationId}
                              initialSearchTerm={prefillEmployeeName}
                            />
                          </Box>
                          <Button variant="contained" onClick={() => setOpenQuickAddEmployee(true)}
                            sx={{
                              minWidth: 'auto', height: '56px', px: 2, borderRadius: '10px',
                              color: 'white', textTransform: 'none', fontWeight: 600,
                            }}>
                            Quick Add
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{
                          display: 'flex', alignItems: 'center', p: 2, borderRadius: '8px',
                          bgcolor: 'action.hover', border: '1px solid', borderColor: 'info.light', boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: '#00838f', overflow: 'hidden' }}>
                            {selectedEmployee.photo ? (
                              <AuthenticatedImage
                                src={selectedEmployee.photo}
                                alt={selectedEmployee.nameEn || 'Employee'}
                                employeeId={selectedEmployee.uid || selectedEmployee.employeeId || ''}
                                maxSize={80}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                              />
                            ) : (
                              <MdPerson size={24} />
                            )}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#263238' }}>
                              {selectedEmployee.nameEn || `${selectedEmployee.firstNameEn || ''} ${selectedEmployee.lastNameEn || ''}`}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              {selectedEmployee.employeeId && (
                                <Typography variant="caption" sx={{ color: '#0277bd', fontWeight: 500 }}>
                                  ID: {selectedEmployee.employeeId}
                                </Typography>
                              )}
                              {selectedEmployee.department && (
                                <Typography variant="caption" sx={{ color: '#00796b', fontWeight: 500 }}>
                                  {selectedEmployee.department?.departmentName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Button size="small" variant="outlined" onClick={handleEmployeeRemove}
                            sx={{ minWidth: 'auto', ml: 1, fontSize: '0.75rem', textTransform: 'none', borderRadius: '4px', py: 0.5, px: 1, color: 'text.primary', borderColor: 'text.primary' }}>
                            Change
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Field>
              </Box>
            </Box>

            {/* Customer/Employee Information (static fields) */}
            {isUidready && (
              <Box sx={cardStyle}>
                <Box sx={headerStyle}>
                  <Typography fontSize={16} variant="h6" color="text.primary">Employee Information</Typography>
                </Box>
                <Divider sx={{ borderColor: 'divider' }} />
                <Box sx={cardContentStyle}>
                  <CustomerTextField
                    staticFields={staticFields} isUidready={isUidready}
                    formikRef={formikRef} setFieldValue={setFieldValue}
                    uploadingDocs={uploadingStaticDocs}
                    onUpload={_onStaticFieldDocUpload}
                    onUploadComplete={_onStaticFieldDocUploadComplete}
                    formikValues={values}
                    employeeUid={selectedEmployee?.uid || ''}
                    onFileUpload={_onFileUpload}
                    onFileRemove={_onFileRemove}
                    selectedEmployee={selectedEmployee}
                    fields={fields}
                  />
                </Box>
              </Box>
            )}

            {/* Categorized service fields */}
            {fields.length > 0 && selectedEmployee && selectedEmployee.uid && (
              <Box sx={{ mt: 3 }}>
                {(() => {
                  const groupedFields = {};
                  const staticDocumentFields = STATIC_DOCUMENT_FIELDS;

                  const validFields = fields.filter(field => {
                    if (!field || !field.name || field.name === 'undefined') return false;
                    if (field.label && (field.label.includes('undefined') || field.label.trim() === '')) return false;
                    if (staticDocumentFields.includes(field.name)) return false;
                    return true;
                  });

                  validFields.forEach(field => {
                    const category = STATIC_FIELD_CATEGORY_MAP[field.name] || 'SERVICE_SPECIFIC';
                    if (!groupedFields[category]) groupedFields[category] = [];
                    groupedFields[category].push(field);
                  });

                  const categoryOrder = fieldCategories.length > 0
                    ? fieldCategories.filter(c => c.code !== 'PERSONAL_INFO').sort((a, b) => a.displayOrder - b.displayOrder).map(c => c.code)
                    : ['CONTACT_INFO', 'IDENTIFICATION', 'ADDRESS', 'DOCUMENTS', 'SERVICE_SPECIFIC', 'OTHER'];

                  const categoryDisplayNames = {};
                  if (fieldCategories.length > 0) {
                    fieldCategories.forEach(c => { categoryDisplayNames[c.code] = c.displayName; });
                  } else {
                    Object.keys(CATEGORY_DISPLAY_NAMES).forEach(k => { categoryDisplayNames[k] = CATEGORY_DISPLAY_NAMES[k]; });
                  }

                  const sortedKeys = Object.keys(groupedFields).sort((a, b) => {
                    const iA = categoryOrder.indexOf(a);
                    const iB = categoryOrder.indexOf(b);
                    if (iA >= 0 && iB >= 0) return iA - iB;
                    if (iA >= 0) return -1;
                    if (iB >= 0) return 1;
                    return a.localeCompare(b);
                  });

                  if (isUidready && groupedFields['PERSONAL_INFO']) delete groupedFields['PERSONAL_INFO'];

                  return sortedKeys.map(category => {
                    if (!groupedFields[category] || !Array.isArray(groupedFields[category])) return null;
                    const fieldsForCat = groupedFields[category].filter(f => {
                      if (!f || typeof f !== 'object') return false;
                      if (populatedFields.has(f.name)) return false;
                      return true;
                    });
                    if (fieldsForCat.length === 0) return null;

                    const displayName = categoryDisplayNames[category] ||
                      CATEGORY_DISPLAY_NAMES[category] ||
                      category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());

                    return (
                      <Box key={category} sx={cardStyle} mb={2}>
                        <Box sx={headerStyle}>
                          <Typography fontSize={16} variant="h6" color="text.primary">{displayName}</Typography>
                        </Box>
                        <Divider sx={{ borderColor: 'divider' }} />
                        <Box sx={cardContentStyle}>
                          <Grid container spacing={2}>
                            {fieldsForCat.map((field, index) => _renderField(field, index))}
                          </Grid>
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
            )}

            {/* Charges & Assignment */}
            {chargeMapping && (
              <Box my={3}>
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  py: 1.5, px: 1, borderBottom: '1px solid', borderBottomColor: 'divider',
                  borderRadius: 1, mb: 2
                }}>
                  <Typography variant="subtitle2" sx={{ fontSize: pxToRem(14), fontWeight: 'bold' }}>
                    Ticket assignment
                  </Typography>
                  <Chip
                    icon={<AccessTimeIcon />}
                    label="Assigned to Thaklis"
                    size="small" color="secondary" variant="outlined"
                    sx={{ height: '24px', minWidth: '150px' }}
                  />
                </Box>

                <Divider sx={{ my: 1 }}><Typography fontSize={14}>Charges</Typography></Divider>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption">Govt. Charge</Typography>
                    <Typography variant="h6" fontSize={14}>
                      {chargeMapping.govtCharge}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption">Service Charge</Typography>
                    <Typography variant="h6" fontSize={14}>
                      {chargeMapping.serviceCharge}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption">Tax On Service Charge</Typography>
                    <Typography variant="h6" fontSize={14}>
                      {chargeMapping.taxOnServiceCharge}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Submit Button */}
            <Box display="flex" justifyContent="flex-end" pt={3}>
              <Button
                variant="contained"
                disabled={!dirty || !isValid || !_isFieldsAreValid()}
                type="submit"
                sx={{
                  color: 'white',
                  '&:disabled': { bgcolor: 'action.disabled', color: 'text.disabled' },
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4
                }}
              >
                Submit
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      {/* Customer Creation Dialog */}
      <Dialog open={openCustomerDialog} onClose={() => setOpenCustomerDialog(false)} maxWidth="md" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ bgcolor: 'action.hover', borderBottom: '1px solid #e0e0e0' }}>
          Customer Not Found - Add New Customer
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <DialogContentText sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Customer UID: <Box component="span" sx={{ ml: 1, p: 0.5, bgcolor: '#e3f2fd', borderRadius: 0.5, fontFamily: 'monospace' }}>{newCustomer.uid}</Box>
            </DialogContentText>
          </Box>
          <DialogContentText sx={{ mb: 2 }}>
            Please enter the customer details below.
          </DialogContentText>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField name="firstNameEn" label="First Name" value={newCustomer.firstNameEn} onChange={handleCustomerInputChange} fullWidth required margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="middleNameEn" label="Middle Name" value={newCustomer.middleNameEn} onChange={handleCustomerInputChange} fullWidth margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="lastNameEn" label="Last Name" value={newCustomer.lastNameEn} onChange={handleCustomerInputChange} fullWidth required margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="nameEn" label="Full Name" value={newCustomer.nameEn} onChange={handleCustomerInputChange} fullWidth margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="mobileNumber" label="Mobile Number" value={newCustomer.mobileNumber} onChange={handleCustomerInputChange} fullWidth required margin="dense" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="email" label="Email" type="email" value={newCustomer.email} onChange={handleCustomerInputChange} fullWidth margin="dense" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCustomerDialog(false)} color="secondary">Cancel</Button>
          <Button onClick={handleCreateCustomer} variant="contained"
            disabled={!newCustomer.firstNameEn || !newCustomer.lastNameEn || !newCustomer.mobileNumber}>
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Employee Dialog */}
      <QuickEmployeeAddDialog
        open={openQuickAddEmployee}
        onClose={() => setOpenQuickAddEmployee(false)}
        onEmployeeAdded={handleEmployeeAdded}
        organizationId={organizationId}
      />

      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};


/**
 * Main CreateTicket page with organization selector
 */
const CreateTicket = () => {
  const [mappings, setMappings] = useState([]);
  const [selectedMappingId, setSelectedMappingId] = useState('');
  const [selectedOrgName, setSelectedOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [initializingThaklis, setInitializingThaklis] = useState(false);
  const [thaklisReady, setThaklisReady] = useState(false);
  const [error, setError] = useState(null);

  // Load user's mapped organizations
  useEffect(() => {
    const fetchMappings = async () => {
      try {
        setLoading(true);
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const typingCenterId = currentUser?.typingCenterId;
        let response;
        if (typingCenterId) {
          response = await ApiService.get(`/api/mappings?typingCenterId=${typingCenterId}`);
        } else {
          response = await ApiService.get('/api/mappings');
        }
        setMappings(response?.data?.data || []);
      } catch (err) {
        console.error('Failed to load organizations:', err);
        setError('Failed to load organizations.');
      } finally {
        setLoading(false);
      }
    };
    fetchMappings();
    return () => { ThaklisApiService.reset(); };
  }, []);

  const handleOrganizationChange = useCallback(async (mappingId) => {
    setSelectedMappingId(mappingId);
    setThaklisReady(false);
    setError(null);
    if (!mappingId) return;

    const mapping = mappings.find(m => (m.id || m.mappingId) === mappingId);
    setSelectedOrgName(mapping?.organization_name || mapping?.organizationName || mapping?.organization?.name || 'Organization');

    setInitializingThaklis(true);
    try {
      await ThaklisApiService.initForOrganization(mappingId);
      setThaklisReady(true);
    } catch (err) {
      console.error('Failed to initialize Thaklis API:', err);
      const status = err?.response?.status;
      let message;
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (status === 400) {
        message = 'Organization credentials are invalid or missing. Please configure valid Thaklis credentials for this organization.';
      } else if (status === 401 || status === 403) {
        message = 'Not authorized to access this organization. Please check your permissions.';
      } else if (status === 404) {
        message = 'Organization credentials not found. Please set up Thaklis credentials for this organization first.';
      } else if (!err?.response) {
        message = 'Unable to reach the server. Please check your network connection and try again.';
      } else {
        message = 'Failed to connect to Thaklis. Please try again or contact support.';
      }
      setError(message);
    } finally {
      setInitializingThaklis(false);
    }
  }, [mappings]);

  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  if (!can(sessionUser, 'thaklis_features', 'write')) {
    return <Navigate to="/tickets" replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>Create Ticket</Typography>

      {/* Organization Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select Organization</InputLabel>
            <Select value={selectedMappingId} label="Select Organization"
              onChange={(e) => handleOrganizationChange(e.target.value)}>
              {mappings.map(m => (
                <MenuItem key={m.id || m.mappingId} value={m.id || m.mappingId}>
                  {m.organization_name || m.organizationName || m.organization?.name || m.organization_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {initializingThaklis && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Connecting to Thaklis...</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Ticket Creation Form */}
      {thaklisReady && (
        <FieldStructureProvider>
          <TicketForm organizationId={selectedMappingId} organizationName={selectedOrgName} />
        </FieldStructureProvider>
      )}

      {!selectedMappingId && !loading && (
        <Alert severity="info">Please select an organization to create a ticket.</Alert>
      )}
    </Box>
  );
};

export default CreateTicket;
export { TicketForm };
